"use client";

import React, { useEffect, useState } from "react";
import { ImageSearchRequest } from "@/restapi/image";
import { useImageSearch } from "@/hooks/image/useImageSearch";
import { FilterImageCategory, TabItem } from "./hooks/useGalleryCategory";
import RandomizedMasonryGrid from "./grids/RandomizedMasonryGrid";
import GalleryGrid from "./grids/GalleryGrid";
import { ImageGridCardDeleteOptions } from "@/components/ui/card/ImageGridCard";
import { ScreenType } from "@/context/AppContextProvider";
import FullScreenGallery from "./FullScreenGallery";
import { User } from "next-auth";
import { useRouter } from "next/navigation";
import { URLConstants } from "@/constants/URLConstants";

type Props = {
  request: ImageSearchRequest;
  gallaryCategory: FilterImageCategory;
  screenType: ScreenType;
  changeScreenType: (screenType: ScreenType) => void;
  deleteOptions?: ImageGridCardDeleteOptions | undefined;
  className?: string;
  defaultScreenType?: ScreenType;
  refreshInterval?: number;
  user?: User;
  setTabs?: React.Dispatch<React.SetStateAction<TabItem[]>>
  bulkMode?: boolean;
  checkedIds?: number[];
  onToggleCheck?: (imageId: number) => void;
};

const InfiniteScrollGallery: React.FC<Props> = ({
  gallaryCategory,
  request,
  screenType,
  changeScreenType,
  deleteOptions,
  className,
  defaultScreenType = "grid",
  refreshInterval,
  user,
  bulkMode = false,
  checkedIds = [],
  onToggleCheck,
}) => {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const router = useRouter();

  const { items, isError, isLoading, isValidating, hasMore, loadMore, lockedCound, } = useImageSearch(
    {
      ...request,
      refreshInterval,
    },
    1
  );

  useEffect(() => {
    if ((lockedCound ?? 0) > 0) {
      router.push(URLConstants.image.lockedImageManagement.pathname);
    }
  }, [lockedCound, router]);

  const handleChangeScReenType = (imageId: string) => {
    setSelectedImageId(imageId);
    changeScreenType("fullscreen");
  };

  const revertScreenType = () => {
    changeScreenType(defaultScreenType);
  };

  if (isError) {
    return <></>;
  }

  // browser back でもデータがあれば content を表示（revalidating 中でも OK）
  const shouldShowContent = items && items.length > 0 && !isError;
  const shouldShowLoading = isLoading && !shouldShowContent;

  if (shouldShowLoading) {
    return <></>;
  }

  if (!items) {
    return null;
  }

  switch (screenType) {
    case "fullscreen":
      return (
        <FullScreenGallery
          user={user}
          images={items}
          selectedImageId={selectedImageId ?? undefined}
          filterImageCategory={gallaryCategory}
          loadMore={loadMore}
          hasMore={hasMore}
          revertScreenType={revertScreenType}
        />
      );

    case "grid":
      return (
        <GalleryGrid
          user={user}
          items={items}
          loadMore={loadMore}
          hasMore={hasMore}
          category={gallaryCategory}
          handleChangeScReenType={handleChangeScReenType}
          className={className}
          deleteOptions={deleteOptions}
          bulkMode={bulkMode}
          checkedIds={checkedIds}
          onToggleCheck={onToggleCheck}
        />
      );

    case "random":
      return (
        <RandomizedMasonryGrid
          user={user}
          items={items}
          loadMore={loadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          category={gallaryCategory}
          handleChangeScReenType={handleChangeScReenType}
          className={className}
        />
      );

    default:
      return <></>;
  }
};

export default InfiniteScrollGallery;